'use client'

/**
 * Hooks for skeleton fallbacks - read preview data from _preview cache keys.
 *
 * These hooks read from the _preview cache keys (separate from detail keys).
 * Preview data is seeded from list views to show partial info while Suspense fetches.
 *
 * Architecture:
 * - List page seeds _preview key with partial data
 * - Navigate to detail page → Suspense suspends
 * - Skeleton fallback reads from _preview key
 * - Once query resolves → full component renders with full data
 */
import { useQueryClient } from '@tanstack/react-query'

import type {
  AgencyProfile,
  Award,
  PersonProfile,
  RecipientProfile,
} from '@/types/usaspending'

import { usaspendingKeys } from './queryKeys'

/**
 * Read preview award data for skeleton rendering.
 * Returns Partial<Award> seeded from list view.
 */
export function useSkeletonAwardData(id: string): Partial<Award> | undefined {
  const queryClient = useQueryClient()
  return queryClient.getQueryData<Partial<Award>>(
    usaspendingKeys.awardPreview(id),
  )
}

/**
 * Read preview recipient data for skeleton rendering.
 * Returns Partial<RecipientProfile> seeded from list view.
 */
export function useSkeletonRecipientData(
  id: string,
): Partial<RecipientProfile> | undefined {
  const queryClient = useQueryClient()
  return queryClient.getQueryData<Partial<RecipientProfile>>(
    usaspendingKeys.recipientPreview(id),
  )
}

/**
 * Read preview agency data for skeleton rendering.
 * Returns Partial<AgencyProfile> seeded from list view.
 */
export function useSkeletonAgencyData(
  id: string,
): Partial<AgencyProfile> | undefined {
  const queryClient = useQueryClient()
  return queryClient.getQueryData<Partial<AgencyProfile>>(
    usaspendingKeys.agencyPreview(id),
  )
}

/**
 * Read preview person data for skeleton rendering.
 * Returns Partial<PersonProfile> seeded from list view.
 */
export function useSkeletonPersonData(
  id: string,
): Partial<PersonProfile> | undefined {
  const queryClient = useQueryClient()
  return queryClient.getQueryData<Partial<PersonProfile>>(
    usaspendingKeys.personPreview(id),
  )
}
